/**
 * Guided flow — the state machine behind the kitchen's one-tap assistance modals.
 *
 * A guided flow is: a grid of TOPICS the person can tap instead of typing → optionally a
 * SUBJECT to point at (which order? which item?) → optionally an automatic REMEDY the app
 * tries on its own → a CONFIRM question ("did that fix it?") → either the flow closes as
 * RESOLVED or it ESCALATES to the messenger with a message the app has already written.
 *
 * This module is the engine only. It knows nothing about help, Intercom, orders or cameras:
 * a flow DEFINITION (see helpFlow.js) supplies the topics, and the host component (App.vue)
 * performs the side effects the engine asks for. That split is deliberate:
 *   - the same engine + modal will drive the "AI wrong" flow (the analysis said an item was
 *     missing and the kitchen says it is there), so nothing help-specific may live here;
 *   - every transition is a pure function of (state, input, env), so the whole decision
 *     table is unit-testable in milliseconds — no component mount, no fake Intercom.
 *
 * Contract with the host:
 *   every transition returns `{ state, effect }`. The host stores `state`, runs `effect`
 *   (null, REMEDY, ESCALATE, LOAD or VIEW), and treats `state.step === STEP.CLOSED` as "hide the
 *   modal, `state.outcome` says why". Inputs the engine cannot know (is the messenger loaded? is
 *   an order already open?) travel in `env`, so the engine — not the host — decides the
 *   next step, including the messenger-unavailable dead end.
 *
 * Some topics are answered by LOOKING, not by writing ("I want to see an order's photos"): a
 * `view` topic lists subjects the host has to fetch (LOAD), and picking one opens it (VIEW)
 * instead of writing a ticket. Support stays one tap away from there, never the default.
 *
 * A topic about an order that has usually LEFT the screen ("the app says an item is missing and it
 * is in the bag", "an order left without a photo") lists what the host fetches too (`fetch`), for
 * the same reason; and when the order is not in the list, `askCode` asks for its code (CODE) instead
 * of writing to support without it — the question support otherwise has to ask back.
 *
 * A remedy may reload the page (that IS the fix for most "no orders" reports), so the flow
 * has to survive a reload: `savePendingRemedy` / `takePendingRemedy` persist just enough to
 * come back at the CONFIRM step, and `resumeFlow` rebuilds the state from that record.
 */

export const STEP = Object.freeze({
  CHOOSE: 'choose',           // the topic grid
  PICK: 'pick',               // the topic needs a subject and none is implied by context
  COMPOSE: 'compose',         // free text: the person types the problem in OUR textarea
  WORKING: 'working',         // an automatic remedy is running (the page may be reloading)
  CONFIRM: 'confirm',         // "did that fix it?"
  VIEW: 'view',               // a `view` topic: the picked subject is shown (an order's photos)
  CODE: 'code',               // the order is not in the list: the person types its code
  UNAVAILABLE: 'unavailable', // wanted to escalate, but the messenger cannot be reached
  CLOSED: 'closed',
});

export const OUTCOME = Object.freeze({
  RESOLVED: 'resolved',   // the remedy worked — no ticket written
  ESCALATED: 'escalated', // handed to the messenger with the composed message
  DISMISSED: 'dismissed', // closed without finishing (the ✕, Escape, or a tap outside)
});

export const EFFECT = Object.freeze({
  REMEDY: 'remedy',     // host runs `effect.remedy`, then calls remedyDone() (or resumeFlow after a reload)
  ESCALATE: 'escalate', // host composes the message and opens the messenger; the flow is already CLOSED
  LOAD: 'load',         // host fetches the subjects the PICK step lists — they are not on screen
  VIEW: 'view',         // host shows `state.subject` (fetching whatever it still lacks)
});

export const UNAVAILABLE_REASON = Object.freeze({
  OFFLINE: 'offline',     // the tablet has no connectivity — retrying later can work
  MESSENGER: 'messenger', // online, but the messenger SDK never loaded — a reload is the fix
});

const NOOP = (state) => ({ state, effect: null });

/**
 * A fresh flow, sitting on the topic grid.
 * @param {string} flowId   which definition this is ('help', later 'ai_wrong') — also keys the reload record
 * @param {object} [opts]
 * @param {string} [opts.source]  where it was opened from (telemetry only)
 * @param {number} [opts.now]
 */
export function createFlow(flowId, { source = null, now = Date.now() } = {}) {
  return {
    flowId,
    source,
    step: STEP.CHOOSE,
    topic: null,          // the chosen topic definition
    subject: null,        // what the topic is about (an order descriptor, or a list of them)
    subjectAsked: false,  // the PICK step already ran, so escalate must not send us back to it
    text: null,           // what the person typed on the COMPOSE step, for a free-text topic
    remedy: null,         // remedy id while/after one ran
    remedyFixed: null,    // the CONFIRM answer: true / false / null (not asked)
    fromView: false,      // the hand-over was asked for while looking at the subject (VIEW)
    evidence: null,       // host-provided facts shown on CONFIRM and attached to the message
    unavailableReason: null,
    dismissedFrom: null,  // the step the person was on when they dismissed (the "too many options?" number)
    outcome: null,
    openedAt: now,
  };
}

/**
 * The person tapped a topic.
 * @param {object} topic  a definition entry:
 *   `{ id, subject?, subjectWhen?, multi?, remedy?, free? }`
 *   - subject: what the topic is about ('order', 'platform'...). The host decides what to list.
 *   - subjectWhen: 'choose' (default) asks before anything else; 'escalate' asks only when the
 *     message is about to be written. That second mode exists for a topic that has an automatic
 *     remedy: asking which aggregator BEFORE reloading charges a question to everyone, when the
 *     reload fixes it for most and the answer is only ever needed in the ticket.
 *   - multi: the answer is a list ("which of these orders should not be here"), not one item.
 *   - free: the person types the problem instead of picking it.
 *   - view: the answer is something to LOOK at (an order's photos), not a ticket to write.
 *   - fetch: the PICK list is one the host fetches (LOAD), not what is on screen — the order has
 *     usually left it. The host decides what to fetch; the engine only asks for it.
 *   - askCode: "it is not in the list" asks for the code (CODE) instead of escalating without one.
 * @param {object} env    `{ subject, candidates, canEscalate, online }`
 *   - subject: the subject the context already implies (the order open in the camera), if any;
 *     it is stored on the state so the host never has to re-derive it at escalation time
 *   - candidates: how many subjects the person could pick from; 0 → skip the PICK step
 *     (ignored for a `view` topic: its list is fetched, so the count is unknown until LOAD)
 *   - canEscalate: the host can actually hand over (for help: the messenger widget is loaded)
 */
export function chooseTopic(state, topic, env = {}) {
  if (state.step !== STEP.CHOOSE || !topic) return NOOP(state);
  const next = { ...state, topic };
  // Texto libre: no hay nada que elegir, hay algo que contar. Se escribe en NUESTRO campo (con
  // autofoco, así que sale el teclado) en vez de mandarlos al redactor de Intercom a buscarlo.
  if (topic.free) return { state: { ...next, step: STEP.COMPOSE }, effect: null };
  // Se contesta mirando, no escribiendo. Con el pedido abierto en la cámara se enseña ese; si no,
  // la lista hay que traerla (lo que ya salió no está en pantalla), así que se pide SIEMPRE y el
  // anfitrión la carga: el recuento de candidatos no se conoce hasta que llega.
  if (topic.view) {
    if (env.subject && !topic.multi) return openView({ ...next, subject: env.subject });
    return { state: { ...next, step: STEP.PICK }, effect: { type: EFFECT.LOAD } };
  }
  // La lista que hay que traer: con el pedido abierto en la cámara, ese es el sujeto (la persona lo
  // está mirando); si no, se pide siempre, porque lo que ya salió de la pantalla no se puede contar.
  if (topic.fetch && topic.subject && topic.subjectWhen !== 'escalate') {
    if (env.subject && !topic.multi) return proceedWithSubject({ ...next, subject: env.subject }, env);
    return { state: { ...next, step: STEP.PICK }, effect: { type: EFFECT.LOAD } };
  }
  if (topic.subject && topic.subjectWhen !== 'escalate') {
    if (env.subject && !topic.multi) return proceedWithSubject({ ...next, subject: env.subject }, env);
    if ((env.candidates || 0) > 0) return { state: { ...next, step: STEP.PICK }, effect: null };
  }
  return proceedWithSubject(next, env);
}

/**
 * The text typed on the COMPOSE step. Empty text is not a message worth sending, so it is a no-op:
 * the modal keeps its send button disabled and this is the belt to that braces.
 */
export function submitText(state, text, env = {}) {
  if (state.step !== STEP.COMPOSE) return NOOP(state);
  const trimmed = (text || '').trim();
  if (!trimmed) return NOOP(state);
  return escalate({ ...state, text: trimmed }, env);
}

/**
 * The person answered the PICK step. `subject === null` is a legitimate answer ("it is not in the
 * list") — the flow escalates without one rather than blocking. For a `multi` topic the answer is
 * an array; an empty array is treated as "none of them", i.e. the same as null.
 */
export function pickSubject(state, subject, env = {}) {
  if (state.step !== STEP.PICK) return NOOP(state);
  const answer = Array.isArray(subject) ? (subject.length ? subject : null) : (subject || null);
  // "No está en la lista", en un tema que necesita el pedido: se pregunta su código antes de
  // escribir a soporte, que si no es lo primero que tiene que preguntar de vuelta.
  if (!answer && state.topic?.askCode && !state.topic?.multi) {
    return { state: { ...state, step: STEP.CODE, subject: null }, effect: null };
  }
  // Un tema de mirar abre lo elegido. "No está en la lista" sigue su camino de siempre, hacia
  // soporte: la persona lo busca y la app no lo tiene, así que alguien tiene que ayudarla.
  if (state.topic?.view && answer && !Array.isArray(answer)) {
    return openView({ ...state, subject: answer, subjectAsked: true });
  }
  // `subjectAsked` cierra la puerta: ya se preguntó, así que escalate no puede volver a mandar
  // aquí y hacer un bucle cuando la respuesta es "no está en la lista".
  return proceedWithSubject({ ...state, subject: answer, subjectAsked: true }, env);
}

/**
 * The code typed on the CODE step. The host hands it in already cleaned (or null for "I do not
 * have it"): the engine knows nothing of what a code looks like. Either way the flow goes on — a
 * code nobody has is no reason to leave the kitchen without help — with the typed code as the
 * subject, marked as typed so the message can say it was not picked from the orders.
 */
export function submitCode(state, code, env = {}) {
  if (state.step !== STEP.CODE) return NOOP(state);
  const typed = typeof code === 'string' && code ? code : null;
  const subject = typed ? { kind: 'code', code: typed } : null;
  return proceedWithSubject({ ...state, subject, subjectAsked: true }, env);
}

/** A `view` topic's subject is settled: show it. The host fetches whatever it still lacks. */
function openView(state) {
  return { state: { ...state, step: STEP.VIEW }, effect: { type: EFFECT.VIEW } };
}

/**
 * From the VIEW step, the person wants to talk to support about what they are looking at. Goes
 * through the same gate as any hand-over (UNAVAILABLE when the messenger is not really there),
 * and records that it came from looking, so the message can say so instead of repeating the
 * request the app has just answered.
 */
export function askAboutSubject(state, env = {}) {
  if (state.step !== STEP.VIEW) return NOOP(state);
  return escalate({ ...state, fromView: true }, env);
}

/**
 * The subject is settled: run the topic's remedy if it has one, otherwise escalate.
 *
 * `state.remedy` already set means the remedy ALREADY RAN and this is the second pass through
 * here — the subject was asked late (subjectWhen: 'escalate'), after the confirm step. Without
 * this guard the flow would fire the remedy again and reload the app a second time, right after
 * the person told us the reload had not fixed anything.
 */
function proceedWithSubject(state, env) {
  const remedy = state.remedy ? null : (state.topic?.remedy || null);
  if (remedy) {
    return {
      state: { ...state, step: STEP.WORKING, remedy },
      effect: { type: EFFECT.REMEDY, remedy },
    };
  }
  return escalate(state, env);
}

/** The remedy finished in-page (no reload). `evidence` is whatever the host wants CONFIRM to show. */
export function remedyDone(state, evidence = null) {
  if (state.step !== STEP.WORKING) return NOOP(state);
  return { state: { ...state, step: STEP.CONFIRM, evidence }, effect: null };
}

/** The CONFIRM answer. Yes closes the flow as RESOLVED; no escalates with the remedy on record. */
export function answerConfirm(state, fixed, env = {}) {
  if (state.step !== STEP.CONFIRM) return NOOP(state);
  const next = { ...state, remedyFixed: Boolean(fixed) };
  if (fixed) return close(next, OUTCOME.RESOLVED);
  return escalate(next, env);
}

/**
 * Hand over. Only possible when the host says it can (`env.canEscalate` — for the help flow,
 * the messenger widget is really loaded, not just its loader queue); otherwise the flow parks
 * on UNAVAILABLE instead of closing silently — a tap that lands nowhere is indistinguishable
 * from a dead button, and that was the old behaviour.
 */
export function escalate(state, env = {}) {
  if (state.step === STEP.CLOSED) return NOOP(state);
  // El sujeto se pide ANTES de escribir cuando el tema lo necesita y todavía no se ha preguntado.
  // Es lo que permite que "no me aparece un pedido" recargue primero (que arregla la mayoría) y
  // solo pregunte la agregadora a quien de verdad va a acabar en un tique.
  if (state.topic?.subject && !state.subject && !state.subjectAsked && (env.candidates || 0) > 0) {
    return { state: { ...state, step: STEP.PICK }, effect: null };
  }
  if (!env.canEscalate) {
    const reason = env.online === false ? UNAVAILABLE_REASON.OFFLINE : UNAVAILABLE_REASON.MESSENGER;
    return { state: { ...state, step: STEP.UNAVAILABLE, unavailableReason: reason }, effect: null };
  }
  return {
    state: { ...state, step: STEP.CLOSED, outcome: OUTCOME.ESCALATED, unavailableReason: null },
    effect: { type: EFFECT.ESCALATE },
  };
}

/** "Retry" on the UNAVAILABLE step — same decision as escalate, re-evaluated with the current env. */
export function retryEscalation(state, env = {}) {
  if (state.step !== STEP.UNAVAILABLE) return NOOP(state);
  return escalate(state, env);
}

/**
 * Back one level from a sub-step: from VIEW to the list it was picked from (fetched again if
 * the host wants — the orders keep moving), from anywhere else to the topic grid. CONFIRM has no
 * "back": the remedy already ran (the page may have reloaded), so the only exits are the two
 * answers or the ✕.
 */
export function goBack(state) {
  // From the code to the list it was not in: the list is still what the person was looking at.
  if (state.step === STEP.CODE) {
    return { state: { ...state, step: STEP.PICK, subject: null }, effect: { type: EFFECT.LOAD } };
  }
  if (state.step === STEP.VIEW) {
    return {
      state: { ...state, step: STEP.PICK, subject: null, fromView: false },
      effect: { type: EFFECT.LOAD },
    };
  }
  if (state.step !== STEP.PICK && state.step !== STEP.UNAVAILABLE && state.step !== STEP.COMPOSE) return NOOP(state);
  // A clean grid: the remedy that ran for the PREVIOUS topic must not follow the next one into
  // its message ("we already reloaded" on a topic that never reloads).
  return {
    state: {
      ...state, step: STEP.CHOOSE, topic: null, subject: null, subjectAsked: false, text: null,
      unavailableReason: null, remedy: null, remedyFixed: null, evidence: null, fromView: false,
    },
    effect: null,
  };
}

/** Closed without finishing — from any step. */
export function dismiss(state) {
  if (state.step === STEP.CLOSED) return NOOP(state);
  return close({ ...state, dismissedFrom: state.step }, OUTCOME.DISMISSED);
}

function close(state, outcome) {
  return { state: { ...state, step: STEP.CLOSED, outcome }, effect: null };
}

// ---- surviving a reload -------------------------------------------------------------------

const PENDING_KEY_PREFIX = 'bronze_guided_flow_pending';
export const PENDING_MAX_AGE_MS = 10 * 60 * 1000;

// One slot per flow, so a pending "AI wrong" remedy can never be swallowed by the help flow's
// resume (or vice versa) — each host only ever reads its own.
const pendingKey = (flowId) => `${PENDING_KEY_PREFIX}:${flowId}`;

/**
 * Persist the minimum needed to come back at CONFIRM after the page reloads. sessionStorage
 * on purpose: it survives `location.reload()` but dies with the tab, so a question about a
 * reload nobody remembers can never resurface days later.
 * @param {Storage} storage  sessionStorage (injected for tests)
 * @param {object} record    `{ flowId, topicId, remedy, source?, subject?, before? }`
 */
export function savePendingRemedy(storage, record, now = Date.now()) {
  if (!record || !record.flowId) return false;
  try {
    storage.setItem(pendingKey(record.flowId), JSON.stringify({ ...record, startedAt: now }));
    return true;
  } catch (_) {
    return false; // storage unavailable — the flow simply won't ask; nothing else breaks
  }
}

/**
 * Read AND clear this flow's pending record. Returns null when there is none, when it is
 * malformed, or when it is older than `maxAgeMs` (a stale reload that never came back should
 * not ask).
 */
export function takePendingRemedy(storage, flowId, { now = Date.now(), maxAgeMs = PENDING_MAX_AGE_MS } = {}) {
  if (!flowId) return null;
  let raw = null;
  try {
    raw = storage.getItem(pendingKey(flowId));
    if (raw !== null) storage.removeItem(pendingKey(flowId));
  } catch (_) {
    return null;
  }
  if (!raw) return null;
  try {
    const record = JSON.parse(raw);
    if (!record || typeof record !== 'object' || record.flowId !== flowId || !record.topicId) return null;
    if (typeof record.startedAt !== 'number' || now - record.startedAt > maxAgeMs || now < record.startedAt) return null;
    return record;
  } catch (_) {
    return null;
  }
}

/** Drop this flow's pending record without reading it (sign-out, session expiry). */
export function clearPendingRemedy(storage, flowId) {
  try { storage.removeItem(pendingKey(flowId)); } catch (_) { /* nothing to clear */ }
}

export const FOLLOW_UP = Object.freeze({
  ASK: 'ask',         // open the CONFIRM question now
  RETRY: 'retry',     // something is in the way (order being photographed, another modal) — try later
  EXPIRED: 'expired', // too long since the remedy ran: a stale question would only confuse
});

/**
 * Whether the post-reload question should be asked now, later, or not at all. Pure so the
 * timing rules are testable without a browser: expiry is checked BEFORE busyness, so a tablet
 * that stays busy past the deadline drops the question instead of nagging forever.
 * @param {object} args  `{ now, startedAt, deadlineMs, busy }`
 */
export function followUpDecision({ now = Date.now(), startedAt = 0, deadlineMs = PENDING_MAX_AGE_MS, busy = false } = {}) {
  if (now > startedAt + deadlineMs) return FOLLOW_UP.EXPIRED;
  return busy ? FOLLOW_UP.RETRY : FOLLOW_UP.ASK;
}

/**
 * Rebuild a flow at the CONFIRM step from a pending record — the state the page would have
 * reached had the remedy finished in-page.
 * @param {object} record  from takePendingRemedy
 * @param {object} topic   the definition entry matching record.topicId (host resolves it)
 * @param {object} [opts]  `{ evidence, now }`
 */
export function resumeFlow(record, topic, { evidence = null, now = Date.now() } = {}) {
  return {
    ...createFlow(record.flowId, { source: record.source || null, now }),
    step: STEP.CONFIRM,
    topic,
    subject: record.subject || null,
    remedy: record.remedy || topic?.remedy || null,
    evidence,
    openedAt: record.startedAt || now,
  };
}
